import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Printer, Camera } from "@phosphor-icons/react";
import ReceiptScanDialog from "@/components/ReceiptScanDialog";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Segmented, DateRange, NativeSelect } from "@/components/common";
import { api, money, fmtDate, fmtDateTime, L, addDays, todayStr, qty } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";

export default function DocList({ mode }) {
  const isOrder = mode === "order";
  const base = isOrder ? "/siparisler" : "/faturalar";
  const nav = useNavigate();
  const [scan, setScan] = useState(false);
  const [scanAccounts] = useLoad(() => (scan ? api.get("/accounts").then((r) => r.data) : Promise.resolve([])), [scan]);
  const [scanProducts] = useLoad(() => (scan ? api.get("/products").then((r) => r.data) : Promise.resolve([])), [scan]);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  useQueryParam("tur", setKind);
  const [start, setStart] = useState(addDays(todayStr(), -30));
  const [end, setEnd] = useState(todayStr());
  const [rows] = useLoad(() => api.get(isOrder ? "/orders" : "/invoices", {
    params: { kind: kind || undefined, status: status || undefined, start: start || undefined, end: end || undefined },
  }).then((r) => r.data), [mode, kind, status, start, end]);
  const list = rows || [];
  const [sel, setSel] = useState([]);
  useEffect(() => { setSel([]); }, [kind, status, start, end, mode]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = list.length > 0 && sel.length === list.length;
  const sum = (k) => list.filter((r) => r.kind === k && r.status !== "cancelled").reduce((s, r) => s + r.total_try, 0);
  const word = isOrder ? "Sipariş" : "Fatura";

  return (
    <div>
      <PageHeader eyebrow={isOrder ? "Satış & Alış Siparişleri" : "Satış & Alış Faturaları"} title={isOrder ? "Siparişler" : "Faturalar"}>
        {!isOrder && sel.length > 0 && (
          <Button variant="outline" className="rounded-[4px] border-teal-600 text-teal-700" onClick={() => window.open(`/faturalar-yazdir?ids=${sel.join(",")}&print=1`, "_blank")} data-testid="bulk-print-selected-button">
            <Printer /> Seçilenleri Yazdır ({sel.length})
          </Button>
        )}
        {!isOrder && <Button variant="outline" className="rounded-[4px] border-[#fdba74] text-orange-800" onClick={() => setScan(true)} data-testid="receipt-scan-open-button"><Camera weight="fill" /> Fiş Fotoğrafından</Button>}
        <Button asChild variant="outline" className="rounded-[4px]" data-testid={`new-purchase-${mode}-button`}><Link to={`${base}/yeni?tur=purchase`}><Plus /> Alış {word}</Link></Button>
        <Button asChild className="rounded-[4px]" data-testid={`new-sale-${mode}-button`}><Link to={`${base}/yeni?tur=sale`}><Plus /> Satış {word}</Link></Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label={`${word} Sayısı`} value={list.length} testid="doclist-count" />
        <Kpi label={`Satış ${word} Toplamı`} value={money(sum("sale"))} tone="blue" testid="doclist-sale-total" delay={40} />
        <Kpi label={`Alış ${word} Toplamı`} value={money(sum("purchase"))} tone="amber" testid="doclist-purchase-total" delay={80} />
      </div>
      <div className="panel">
        <div className="flex flex-col gap-3 border-b border-border p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented value={kind} onChange={setKind} prefix="doclist-kind" options={[["", "Tümü"], ["sale", "Satış"], ["purchase", "Alış"]]} />
            {isOrder && (
              <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} className="w-40" data-testid="doclist-status-select">
                <option value="">Tüm durumlar</option><option value="pending">Bekliyor</option><option value="approved">Onaylandı</option><option value="partial">Kısmi Sevk</option><option value="shipped">Sevk Edildi</option><option value="closed">Kapatıldı</option><option value="cancelled">İptal</option>
              </NativeSelect>
            )}
          </div>
          <DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} prefix="doclist" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="doclist-table">
            <thead><tr>{!isOrder && <Th><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : list.map((r) => r.id))} className="h-4 w-4 accent-teal-700" data-testid="doclist-select-all" /></Th>}<Th>No</Th><Th>Tarih</Th><Th>Tür</Th><Th>Cari</Th><Th right>Ara Toplam</Th><Th right>KDV</Th><Th right>Genel Toplam</Th><Th right>TL Karşılığı</Th>{isOrder && <Th right>Sevk</Th>}<Th>{isOrder ? "Durum" : "Sipariş"}</Th>{!isOrder && <Th>Gönderim</Th>}</tr></thead>
            <tbody>
              {rows && list.length === 0 && <Empty cols={isOrder ? 10 : 11} />}
              {list.map((r) => (
                <tr key={r.id} onClick={() => nav(`${base}/${r.id}`)} className="cursor-pointer transition-colors hover:bg-muted/50" data-testid={`doc-row-${r.no}`}>
                  {!isOrder && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-teal-700" data-testid={`doclist-select-${r.no}`} />
                    </Td>
                  )}
                  <Td className="font-num text-xs font-semibold">{r.no}</Td>
                  <Td className="font-num text-xs">{fmtDate(r.date)}</Td>
                  <Td><Tag tone={r.kind}>{L.kind[r.kind]}</Tag></Td>
                  <Td className="font-medium">{r.account_name}</Td>
                  <Td right>{money(r.subtotal, r.currency)}</Td>
                  <Td right>{money(r.vat_total, r.currency)}</Td>
                  <Td right className="font-semibold">{money(r.total, r.currency)}</Td>
                  <Td right className="text-muted-foreground">{money(r.total_try)}</Td>
                  {isOrder && <Td right className="text-xs" data-testid={`doc-row-shipped-${r.no}`}>{qty(r.items.reduce((s, i) => s + (i.shipped_qty || 0), 0))} / {qty(r.items.reduce((s, i) => s + i.qty, 0))}</Td>}
                  <Td>{isOrder ? <Tag tone={r.status}>{L.status[r.status]}</Tag> : <span className="font-num text-xs text-muted-foreground">{r.order_no || "—"}</span>}</Td>
                  {!isOrder && (
                    <Td className="text-xs text-muted-foreground" data-testid={`doc-row-sent-${r.no}`}>
                      {r.last_sent_at ? `${fmtDateTime(r.last_sent_at)}${r.last_sent_to ? ` · ${r.last_sent_to}` : ""}` : "—"}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {!isOrder && <ReceiptScanDialog open={scan} onOpenChange={setScan} accounts={scanAccounts || []} products={scanProducts || []} />}
    </div>
  );
}
